// Thin live store of cybo work_sessions (CYBORG-791 wire). Fed by the
// `cyborg:work_session_changed` broadcast (applyWorkSession) and hydrated by
// client.listWorkSessions (hydrateWorkSessions). The relay already scopes DM
// sessions to their initiator, so whatever reaches this client is visible to it.
// The full sidebar/chips UI is CYBORG-794; this is just the reactive backing store.
import type { WorkSession } from "../core/types.js";

// Per-workspace set of work-session ids the user has DISMISSED from their sidebar
// ("hide from my sidebar", CYBORG-794). Purely client-side + additive (mirrors
// opened-dms.svelte.ts): the relay already scopes WHO sees a session, so this is
// personal decluttering only — never sent to the server, never a status change.
// Persisted to localStorage keyed per workspace so a dismiss survives reload. A
// dismissed session re-surfaces ONLY on genuinely new activity (see applyWorkSession):
// merely navigating to it does not un-dismiss it, which is what breaks the
// open→attach→close→reopen loop.
const DISMISSED_STORAGE_KEY = "cyborg7_dismissed_work_sessions";

// Parse the persisted { workspaceId: sessionId[] } blob into a Set-per-workspace map.
// Best-effort: any malformed shape falls back to empty (matches opened-dms parseOpened).
function loadDismissed(): Record<string, Set<string>> {
  if (typeof localStorage === "undefined") return {};
  try {
    const raw = localStorage.getItem(DISMISSED_STORAGE_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : null;
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return {};
    const next: Record<string, Set<string>> = {};
    for (const [ws, ids] of Object.entries(parsed as Record<string, unknown>)) {
      if (Array.isArray(ids)) {
        next[ws] = new Set(ids.filter((id): id is string => typeof id === "string"));
      }
    }
    return next;
  } catch {
    return {}; // intentional: best-effort hydrate; falls back to empty.
  }
}

function persistDismissed(byWorkspace: Record<string, Set<string>>): void {
  if (typeof localStorage === "undefined") return;
  try {
    const serializable: Record<string, string[]> = {};
    for (const [ws, ids] of Object.entries(byWorkspace)) {
      if (ids.size > 0) serializable[ws] = [...ids];
    }
    localStorage.setItem(DISMISSED_STORAGE_KEY, JSON.stringify(serializable));
  } catch {
    // intentional: best-effort persistence; not user-facing.
  }
}

export const workSessionsState = $state({
  byId: {} as Record<string, WorkSession>,
  // Sidebar "Work Sessions" section (CYBORG-794) load state, set by the
  // listWorkSessions hydration on workspace open (app.svelte selectWorkspace):
  // `loading` drives the skeleton, `error` a quiet inline "couldn't load" state.
  loading: false,
  error: false,
  // workspaceId → Set<dismissed sessionId>. Reassigned wholesale on every mutation
  // so the runes graph observes the change (matches opened-dms). Seeded from
  // localStorage at import so a dismiss survives reload.
  dismissedIds: loadDismissed() as Record<string, Set<string>>,
});

let mutationVersion = 0;
const rowVersion = new Map<string, number>();

/** Capture the live-update frontier before requesting an authoritative workspace snapshot. */
export function beginWorkSessionsSnapshot(): number {
  return mutationVersion;
}

export function isWorkSessionDismissed(workspaceId: string, id: string): boolean {
  return workSessionsState.dismissedIds[workspaceId]?.has(id) ?? false;
}

// Hide a work session from THIS user's sidebar. Looks the session up to find its
// workspace (a row can only be dismissed while it's visible, so it's in byId).
export function dismissWorkSession(id: string): void {
  const session = workSessionsState.byId[id];
  if (!session) return;
  const ws = session.workspaceId;
  if (workSessionsState.dismissedIds[ws]?.has(id)) return;
  const next = new Set(workSessionsState.dismissedIds[ws] ?? []);
  next.add(id);
  workSessionsState.dismissedIds = { ...workSessionsState.dismissedIds, [ws]: next };
  persistDismissed(workSessionsState.dismissedIds);
}

// Un-dismiss a work session so it (re)appears in this user's sidebar. Called on
// genuinely new activity (applyWorkSession) AND on every OPEN of the session route
// (CYBORG-794 attach-on-open) — the one place that re-attaches a dismissed session
// when the user reopens it, closing the open→attach→×→reopen loop. No-op when it
// isn't dismissed, so it's safe to call on every session view.
export function undismissWorkSession(workspaceId: string, id: string): void {
  if (!workSessionsState.dismissedIds[workspaceId]?.has(id)) return;
  const next = new Set(workSessionsState.dismissedIds[workspaceId]);
  next.delete(id);
  workSessionsState.dismissedIds = { ...workSessionsState.dismissedIds, [workspaceId]: next };
  persistDismissed(workSessionsState.dismissedIds);
}

/**
 * Reconcile one workspace against a server snapshot without losing broadcasts that
 * landed while the request was in flight. A row mutated after `startedAtVersion`
 * outranks the older snapshot; untouched rows absent from the snapshot are pruned.
 */
export function hydrateWorkSessions(
  workspaceId: string,
  sessions: WorkSession[],
  startedAtVersion: number,
): void {
  const next = { ...workSessionsState.byId };
  const snapshotIds = new Set(sessions.map((session) => session.id));
  for (const [id, existing] of Object.entries(next)) {
    if (
      existing.workspaceId === workspaceId &&
      !snapshotIds.has(id) &&
      (rowVersion.get(id) ?? 0) <= startedAtVersion
    ) {
      delete next[id];
      rowVersion.delete(id);
    }
  }
  for (const session of sessions) {
    if (session.workspaceId !== workspaceId) continue;
    if ((rowVersion.get(session.id) ?? 0) > startedAtVersion) continue;
    const previous = next[session.id];
    next[session.id] =
      session.originPrompt == null && previous?.originPrompt != null
        ? { ...session, originPrompt: previous.originPrompt }
        : session;
    rowVersion.set(session.id, startedAtVersion);
  }
  workSessionsState.byId = next;
}

/** Upsert one session from a live change broadcast. */
export function applyWorkSession(session: WorkSession): void {
  const prev = workSessionsState.byId[session.id];
  // Genuinely NEW activity (a new turn, or a flip back to waiting_approval) un-dismisses
  // the row so it reappears. A no-op broadcast (same status AND updatedAt — nothing
  // actually changed, e.g. one echoed by merely opening/navigating to the session)
  // leaves a dismissed row hidden. That's the loop-breaker: open→attach→close→reopen
  // must not resurrect a session the user dismissed.
  if (prev && (prev.status !== session.status || prev.updatedAt !== session.updatedAt)) {
    undismissWorkSession(session.workspaceId, session.id);
  }
  // The FOUNDING ASK is set once and immutable server-side, but this apply REPLACES the
  // row wholesale — so any frame built without it (an older relay mid rolling-deploy, a
  // settle broadcast off a row whose origin hasn't been mirrored yet) would blank the
  // thread's birth-row ask on a client that already had it. Keep what we know; a real
  // value on the frame always wins.
  let next =
    session.originPrompt == null && prev?.originPrompt != null
      ? { ...session, originPrompt: prev.originPrompt }
      : session;
  // A daemon's frame carries no archive mark (the relay owns it): keep the one we know.
  if (session.archiveMark === undefined && prev?.archiveMark !== undefined) {
    next = { ...next, archiveMark: prev.archiveMark, archiveMarkedAt: prev.archiveMarkedAt };
  }
  rowVersion.set(session.id, ++mutationVersion);
  workSessionsState.byId = { ...workSessionsState.byId, [session.id]: next };
}

export function workSessionsForWorkspace(workspaceId: string): WorkSession[] {
  return Object.values(workSessionsState.byId).filter((s) => s.workspaceId === workspaceId);
}
